import { calculateRayCoordinates } from "@examspark/common/dist/utils/geom";
import { Cartesian, Stem, StemCartesianGraph } from "@examspark/types";

import { getNewScore } from "./utils";

export const scoreCartesianGraph = (
  expected: StemCartesianGraph,
  actual: Partial<Stem.Response>,
) => {
  const { scoring, config } = expected;
  const score = getNewScore();
  score.max = scoring?.shapes?.length || 0;
  if (!actual?.shapes) return score;

  for (const actualShape of actual.shapes) {
    const findModelShape = scoring?.shapes?.find((expectedShape) => {
      if (actualShape.type !== expectedShape.type) return false;

      switch (actualShape.type) {
        case "point":
          if (expectedShape.type === "point") {
            if (
              actualShape.coord.x === expectedShape.coord.x &&
              actualShape.coord.y === expectedShape.coord.y
            ) {
              return true;
            }
          }
          break;
        case "circle":
          if (expectedShape.type === "circle") {
            if (actualShape.coords[0].x !== expectedShape.coords[0].x)
              return false;
            if (actualShape.coords[0].y !== expectedShape.coords[0].y)
              return false;

            const actualRadius = calculateDistance(
              actualShape.coords[0],
              actualShape.coords[1],
            );
            const expectedRadius = calculateDistance(
              expectedShape.coords[0],
              expectedShape.coords[1],
            );

            if (actualRadius.toFixed(3) !== expectedRadius.toFixed(3))
              return false;

            return true;
          }
          break;
        case "line":
          if (expectedShape.type === "line") {
            const actualSlope = Number(
              getSlope(actualShape.coords[0], actualShape.coords[1]).toFixed(3),
            );
            const expectedSlope = Number(
              getSlope(
                expectedShape.coords[0],
                expectedShape.coords[1],
              ).toFixed(3),
            );

            if (actualSlope !== expectedSlope) return false;
            if ([Infinity, -Infinity].includes(actualSlope)) {
              if (expectedShape.coords[0].x !== actualShape.coords[0].x) {
                return false;
              }
            } else if (actualSlope === 0) {
              if (expectedShape.coords[0].y !== actualShape.coords[0].y) {
                return false;
              }
            } else {
              const expectedIntercept = getYInterceptAtX(
                expectedShape.coords[0],
                expectedShape.coords[1],
                0,
              );
              const actualIntercept = getYInterceptAtX(
                actualShape.coords[0],
                actualShape.coords[1],
                0,
              );

              if (expectedIntercept !== actualIntercept) return false;
            }

            return true;
          }

          break;
        case "ray": {
          if (expectedShape.type === "ray") {
            const actualRayCoords = calculateRayCoordinates({
              shape: actualShape,
              x: config.graph.x,
              y: config.graph.y,
            });
            const expectedRayCoords = calculateRayCoordinates({
              shape: expectedShape,
              x: config.graph.x,
              y: config.graph.y,
            });

            if (
              actualRayCoords !== undefined &&
              expectedRayCoords !== undefined
            ) {
              // ray starts at the same point
              if (
                actualRayCoords[0].x === expectedRayCoords[0].x &&
                actualRayCoords[0].y === expectedRayCoords[0].y
              ) {
                // ray goes in the same direction
                const epsilon = 0.00001;
                if (
                  Math.abs(actualRayCoords[1].x - expectedRayCoords[1].x) <
                    epsilon &&
                  Math.abs(actualRayCoords[1].y - expectedRayCoords[1].y) <
                    epsilon
                ) {
                  return true;
                }
              }
            }
          }
          break;
        }
        case "line-segment":
          if (expectedShape.type === "line-segment") {
            return coordMatchAllCoords(
              actualShape.coords,
              expectedShape.coords,
            );
          }
          break;
        case "polygon":
          if (expectedShape.type === "polygon") {
            return (
              coordMatchAllCoords(actualShape.coords, expectedShape.coords) ||
              coordMatchAllCoords(
                actualShape.coords,
                [...expectedShape.coords].reverse(),
              )
            );
          }
          break;
      }
      return false;
    });

    if (!findModelShape) continue;
    score.actual++;
  }

  // subtract 1 for each extra shape
  const expectedShapeCount = scoring?.shapes?.length || 0;
  const actualShapeCount = actual.shapes.length;
  if (actualShapeCount > expectedShapeCount) {
    score.actual -= actualShapeCount - expectedShapeCount;
  }

  return score;
};

const coordsAreEqual = (
  coord1: Cartesian.CartesianCoordinate,
  coord2: Cartesian.CartesianCoordinate,
) => {
  if (coord1.x === coord2.x && coord1.y === coord2.y) return true;
  return false;
};

// check that all coords match in order, even if they start at different points
const coordMatchAllCoords = (
  coords1: Cartesian.CartesianCoordinate[],
  coords2: Cartesian.CartesianCoordinate[],
) => {
  // find the start point
  const startPoint = coords1.findIndex((coord) =>
    coordsAreEqual(coord, coords2[0]),
  );

  if (startPoint === -1) return false;

  // check if the rest of the points match in order
  for (let i = 0; i < coords2.length; i++) {
    const coord1 = coords1[(startPoint + i) % coords1.length];
    const coord2 = coords2[i];

    if (!coordsAreEqual(coord1, coord2)) return false;
  }

  return true;
};

export const calculateDistance = (
  coord1: Cartesian.CartesianCoordinate,
  coord2: Cartesian.CartesianCoordinate,
) => {
  return Math.sqrt(
    Math.pow(coord2.x - coord1.x, 2) + Math.pow(coord2.y - coord1.y, 2),
  );
};

export const getSlope = (
  coord1: Cartesian.CartesianCoordinate,
  coord2: Cartesian.CartesianCoordinate,
) => {
  const result = (coord2.y - coord1.y) / (coord2.x - coord1.x);
  if (result === -Infinity) return Infinity;
  return result;
};

export const getYInterceptAtX = (
  coord1: Cartesian.CartesianCoordinate,
  coord2: Cartesian.CartesianCoordinate,
  x: number,
) => {
  const slope = getSlope(coord1, coord2);
  const yInt = coord1.y - slope * coord1.x;
  const yIntAtX = x * slope + yInt;
  return yIntAtX;
};
